<template>
    <div class="goal-management">
        <!-- 상단 헤더 -->
        <TargetPageHeader title="배출목표 관리" subtitle="조직 목표 및 목표 수준을 정의합니다.">
            <template #actions>
                <button class="goal-btn-save" @click="saveGoal" :disabled="saving || !isFormValid">
                    <span v-if="saving"><i class="fa fa-spinner fa-spin"></i> 저장 중...</span>
                    <span v-else><i class="fa fa-plus"></i> 목표 생성</span>
                </button>
            </template>
        </TargetPageHeader>

        <!-- 입력 패널 영역 -->
        <div class="goal-input-panels">
            <!-- BASIC INFORMATION -->
            <div class="input-panel">
                <h5 class="panel-title">기본 정보</h5>
                <div class="panel-content">
                    <div class="goal-form-group">
                        <label>목표 명칭</label>
                        <input
                            v-model="form.goalName"
                            type="text"
                            class="form-control"
                            placeholder="목표 명칭을 입력하세요."
                        />
                    </div>
                    <div class="goal-form-group">
                        <label>유형</label>
                        <select v-model="form.targetType" class="form-control">
                            <option value="">선택하세요</option>
                            <option v-for="opt in targetTypeOptions" :key="opt.value" :value="opt.value">
                                {{ opt.text }}
                            </option>
                        </select>
                    </div>
                    <div class="goal-form-group">
                        <label>준수 기준</label>
                        <select v-model.number="form.complianceIdx" class="form-control">
                            <option :value="null">선택하세요</option>
                            <option
                                v-for="comp in complianceList"
                                :key="comp.complianceIdx"
                                :value="comp.complianceIdx"
                            >
                                {{ comp.complianceName }}
                            </option>
                        </select>
                    </div>
                    <div class="goal-form-group">
                        <label>소속 조직</label>
                        <select v-model.number="form.orgIdx" class="form-control">
                            <option :value="null">선택하세요 (대시보드 미표시)</option>
                            <option v-for="org in organizationList" :key="org.orgIdx" :value="org.orgIdx">
                                {{ org.orgName || org.orgCode || `조직 #${org.orgIdx}` }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>

            <!-- COMPARATIVE DATA -->
            <div class="input-panel">
                <h5 class="panel-title">비교 데이터</h5>
                <div class="panel-content comparative-content">
                    <div class="data-subsection">
                        <h6 class="subsection-title">기준연도 데이터 (kgCO2e)</h6>
                        <div class="goal-form-group">
                            <label>기준연도</label>
                            <date-picker
                                v-model="form.baseYear"
                                type="year"
                                format="YYYY"
                                value-type="YYYY"
                                lang="ko"
                                placeholder="기준연도를 선택하세요."
                            ></date-picker>
                        </div>
                        <div class="goal-form-group">
                            <label>기준배출량</label>
                            <div class="input-with-unit">
                                <input
                                    v-model.number="form.baseEmissions"
                                    type="number"
                                    class="form-control"
                                    placeholder="예: 17678517"
                                    step="1"
                                    min="0"
                                />
                                <span class="unit-label">{{ getEmissionUnit() }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="data-subsection highlight-subsection">
                        <h6 class="subsection-title">목표연도 데이터 (kgCO2e)</h6>
                        <div class="goal-form-group">
                            <label>목표연도</label>
                            <date-picker
                                v-model="form.targetYear"
                                type="year"
                                format="YYYY"
                                value-type="YYYY"
                                lang="ko"
                                placeholder="목표연도를 선택하세요."
                            ></date-picker>
                        </div>
                        <div class="goal-form-group">
                            <label>목표배출량</label>
                            <div class="input-with-unit">
                                <input
                                    v-model.number="form.targetEmissions"
                                    type="number"
                                    class="form-control"
                                    placeholder="예: 11215000"
                                    step="1"
                                    min="0"
                                />
                                <span class="unit-label">{{ getEmissionUnit() }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- REMARKS -->
            <div class="input-panel">
                <h5 class="panel-title">비고</h5>
                <div class="panel-content">
                    <div class="remarks-section">
                        <textarea
                            v-model="form.remarks"
                            class="form-control remarks-textarea"
                            placeholder="비고를 입력하세요."
                            style="height: 200px"
                        ></textarea>
                    </div>
                </div>
            </div>
        </div>

        <!-- 등록된 목표 목록 -->
        <div class="goal-list-section">
            <div v-if="loading" class="loading-state">
                <div class="spinner"></div>
                <span>로딩 중...</span>
            </div>
            <div v-else-if="goalList.length === 0" class="empty-state">등록된 목표가 없습니다.</div>
            <div v-else class="goal-table-container">
                <table class="goal-table">
                    <thead>
                        <tr>
                            <th>목표 명칭</th>
                            <th>조직</th>
                            <th>유형</th>
                            <th class="text-right">기준연도</th>
                            <th class="text-right">기준배출량 (kgCO2e)</th>
                            <th class="text-right">목표연도</th>
                            <th class="text-right">목표배출량 (kgCO2e)</th>
                            <th>준수기준</th>
                            <th>감축률</th>
                            <th>비고</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="goal in goalList" :key="goal.targetIdx">
                            <td>{{ goal.goalName }}</td>
                            <td class="text-center">{{ getOrgName(goal.orgIdx) }}</td>
                            <td class="text-center">
                                <span :class="getTargetTypeBadgeClass(goal.targetType)">
                                    {{ getTargetTypeLabel(goal.targetType) }}
                                </span>
                            </td>
                            <td class="text-right">{{ goal.baseYear }}</td>
                            <td class="text-right">{{ formatNumber(goal.baseEmissions) }}</td>
                            <td class="text-right">{{ goal.targetYear }}</td>
                            <td class="target-emission-cell text-right">
                                {{ formatNumber(goal.targetEmissions) }}
                            </td>
                            <td class="text-center">{{ getComplianceName(goal.complianceIdx) || "-" }}</td>
                            <td class="reduction-rate-cell text-center">{{ calculateGoalReductionRate(goal) }}%</td>
                            <td class="text-center">{{ goal.remarks || "-" }}</td>
                            <td>
                                <div class="action-buttons">
                                    <IconButton icon="fas fa-edit" title="수정" @click="editGoal(goal)" />
                                    <IconButton
                                        icon="fas fa-trash"
                                        variant="delete"
                                        title="삭제"
                                        @click="deleteGoal(goal)"
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- 목표 수정 모달 -->
        <b-modal
            v-model="showEditModal"
            title="목표 수정"
            size="lg"
            centered
            ok-title="저장"
            cancel-title="취소"
            :ok-disabled="saving"
            @ok="updateGoal"
        >
            <div class="modal-form">
                <div class="form-row">
                    <div class="form-group">
                        <label>목표 명칭 <span class="required">*</span></label>
                        <input
                            v-model="editForm.goalName"
                            type="text"
                            class="form-control"
                            placeholder="목표 명칭을 입력하세요."
                        />
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group half">
                        <label>유형 <span class="required">*</span></label>
                        <select v-model="editForm.targetType" class="form-control">
                            <option value="">선택하세요</option>
                            <option v-for="opt in targetTypeOptions" :key="opt.value" :value="opt.value">
                                {{ opt.text }}
                            </option>
                        </select>
                    </div>
                    <div class="form-group half">
                        <label>준수 기준 <span class="required">*</span></label>
                        <select v-model.number="editForm.complianceIdx" class="form-control">
                            <option :value="null">선택하세요</option>
                            <option
                                v-for="comp in complianceList"
                                :key="comp.complianceIdx"
                                :value="comp.complianceIdx"
                            >
                                {{ comp.complianceName }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group">
                        <label>소속 조직</label>
                        <select v-model.number="editForm.orgIdx" class="form-control">
                            <option :value="null">선택하세요 (대시보드 미표시)</option>
                            <option v-for="org in organizationList" :key="org.orgIdx" :value="org.orgIdx">
                                {{ org.orgName || org.orgCode || `조직 #${org.orgIdx}` }}
                            </option>
                        </select>
                        <small class="form-text text-muted"
                            >조직을 선택해야 해당 조직 대시보드에 목표가 표시됩니다.</small
                        >
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group half">
                        <label>기준연도 <span class="required">*</span></label>
                        <date-picker
                            v-model="editForm.baseYear"
                            type="year"
                            format="YYYY"
                            value-type="YYYY"
                            lang="ko"
                            placeholder="기준연도를 선택하세요."
                        ></date-picker>
                    </div>
                    <div class="form-group half">
                        <label>기준배출량 <span class="required">*</span></label>
                        <div class="input-with-unit">
                            <input
                                v-model.number="editForm.baseEmissions"
                                type="number"
                                class="form-control"
                                :placeholder="`기준배출량을 입력하세요. (${getEditFormEmissionUnit()})`"
                                step="0.1"
                            />
                            <span class="unit-label">{{ getEditFormEmissionUnit() }}</span>
                        </div>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group half">
                        <label>목표연도 <span class="required">*</span></label>
                        <date-picker
                            v-model="editForm.targetYear"
                            type="year"
                            format="YYYY"
                            value-type="YYYY"
                            lang="ko"
                            placeholder="목표연도를 선택하세요."
                        ></date-picker>
                    </div>
                    <div class="form-group half">
                        <label>목표배출량 <span class="required">*</span></label>
                        <div class="input-with-unit">
                            <input
                                v-model.number="editForm.targetEmissions"
                                type="number"
                                class="form-control"
                                :placeholder="`목표배출량을 입력하세요. (${getEditFormEmissionUnit()})`"
                                step="0.1"
                            />
                            <span class="unit-label">{{ getEditFormEmissionUnit() }}</span>
                        </div>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group">
                        <label>비고</label>
                        <textarea
                            v-model="editForm.remarks"
                            class="form-control"
                            rows="3"
                            placeholder="비고를 입력하세요."
                        ></textarea>
                    </div>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import * as targetService from "../services/targetService";
import { TARGET_STORAGE_UNIT } from "../targetUnits";
import CarbonCodes from "../../../codes";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import IconButton from "../../common/IconButton.vue";
import TargetPageHeader from "./TargetPageHeader.vue";

export default {
    name: "GoalManagement",
    components: {
        DatePicker,
        IconButton,
        TargetPageHeader,
    },
    props: {
        isActive: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            goalList: [],
            complianceList: [],
            organizationList: [],
            loading: false,
            saving: false,
            showEditModal: false,
            form: {
                goalName: "",
                orgIdx: null,
                baseYear: null,
                baseEmissions: null,
                targetYear: null,
                targetEmissions: null,
                targetType: "ABSOLUTE",
                complianceIdx: null,
                intensityDenom: null,
                goalStat: "ACTIVE",
                remarks: null,
            },
            editForm: {
                goalName: "",
                orgIdx: null,
                baseYear: null,
                baseEmissions: null,
                targetYear: null,
                targetEmissions: null,
                targetType: "ABSOLUTE",
                complianceIdx: null,
                intensityDenom: null,
                goalStat: "ACTIVE",
                remarks: null,
            },
            editingGoalIdx: null,
        };
    },
    computed: {
        targetTypeOptions() {
            return CarbonCodes.targetType.getList();
        },
        isFormValid() {
            return (
                this.form.goalName &&
                this.form.baseYear &&
                this.form.baseEmissions &&
                this.form.targetYear &&
                this.form.targetEmissions &&
                this.form.targetType &&
                this.form.complianceIdx
            );
        },
    },
    watch: {
        isActive: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.initialize();
                }
            },
        },
    },
    methods: {
        async initialize() {
            await Promise.all([this.loadGoals(), this.loadCompliances(), this.loadOrganizations()]);
        },
        async loadGoals() {
            this.loading = true;
            try {
                this.goalList = await targetService.fetchGoals();
            } catch (error) {
                console.error("목표 목록 조회 실패:", error);
                this.$bvToast.toast("목표 목록을 불러오는데 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
            } finally {
                this.loading = false;
            }
        },
        async loadCompliances() {
            try {
                this.complianceList = await targetService.fetchCompliances();
            } catch (error) {
                console.error("준수기준 목록 조회 실패:", error);
            }
        },
        async loadOrganizations() {
            try {
                this.organizationList = await targetService.fetchOrganizations({ limit: 100 });
            } catch (error) {
                console.error("조직 목록 조회 실패:", error);
            }
        },
        async saveGoal() {
            if (!this.validateForm()) {
                return;
            }

            this.saving = true;
            try {
                const payload = {
                    goalName: this.form.goalName,
                    orgIdx: this.form.orgIdx,
                    baseYear: this.form.baseYear,
                    baseEmissions: this.form.baseEmissions,
                    targetYear: this.form.targetYear,
                    targetEmissions: this.form.targetEmissions,
                    targetType: this.form.targetType,
                    complianceIdx: this.form.complianceIdx,
                    intensityDenom: this.form.intensityDenom,
                    goalStat: this.form.goalStat,
                    remarks: this.form.remarks,
                };

                if (this.editingGoalIdx) {
                    const result = await targetService.updateGoal(this.editingGoalIdx, payload);
                    this.$bvToast.toast(result?.message || "목표가 수정되었습니다.", {
                        variant: "success",
                        title: "저장 완료",
                    });
                } else {
                    const result = await targetService.createGoal(payload);
                    this.$bvToast.toast(result?.message || "목표가 생성되었습니다.", {
                        variant: "success",
                        title: "저장 완료",
                    });
                }

                this.resetForm();
                await this.loadGoals();
            } catch (error) {
                console.error("목표 저장 실패:", error);
                this.$bvToast.toast(error.message || "목표 저장에 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
            } finally {
                this.saving = false;
            }
        },
        editGoal(goal) {
            this.editingGoalIdx = goal.targetIdx;
            this.editForm = {
                goalName: goal.goalName || "",
                orgIdx: goal.orgIdx ?? null,
                baseYear: goal.baseYear || null,
                baseEmissions: goal.baseEmissions || null,
                targetYear: goal.targetYear || null,
                targetEmissions: goal.targetEmissions || null,
                targetType: goal.targetType || "ABSOLUTE",
                complianceIdx: goal.complianceIdx || null,
                intensityDenom: goal.intensityDenom || null,
                goalStat: goal.goalStat || "ACTIVE",
                remarks: goal.remarks || null,
            };
            this.showEditModal = true;
        },
        async updateGoal(bvModalEvt) {
            if (!this.validateEditForm()) {
                bvModalEvt.preventDefault();
                return;
            }

            this.saving = true;
            try {
                const payload = {
                    goalName: this.editForm.goalName,
                    orgIdx: this.editForm.orgIdx,
                    baseYear: this.editForm.baseYear,
                    baseEmissions: this.editForm.baseEmissions,
                    targetYear: this.editForm.targetYear,
                    targetEmissions: this.editForm.targetEmissions,
                    targetType: this.editForm.targetType,
                    complianceIdx: this.editForm.complianceIdx,
                    intensityDenom: this.editForm.intensityDenom,
                    goalStat: this.editForm.goalStat,
                    remarks: this.editForm.remarks,
                };

                const result = await targetService.updateGoal(this.editingGoalIdx, payload);
                this.$bvToast.toast(result?.message || "목표가 수정되었습니다.", {
                    variant: "success",
                    title: "저장 완료",
                });
                this.showEditModal = false;
                this.editingGoalIdx = null;
                await this.loadGoals();
            } catch (error) {
                console.error("목표 수정 실패:", error);
                this.$bvToast.toast(error.message || "목표 수정에 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
                bvModalEvt.preventDefault();
            } finally {
                this.saving = false;
            }
        },
        validateEditForm() {
            if (!this.editForm.goalName) {
                this.$bvToast.toast("목표 명칭을 입력하세요.", { variant: "warning", title: "입력 오류" });
                return false;
            }
            if (!this.editForm.baseYear) {
                this.$bvToast.toast("기준 연도를 선택하세요.", {
                    variant: "warning",
                    title: "입력 오류",
                });
                return false;
            }
            if (!this.editForm.baseEmissions) {
                this.$bvToast.toast("기준 배출량을 입력하세요.", { variant: "warning", title: "입력 오류" });
                return false;
            }
            if (!this.editForm.targetYear) {
                this.$bvToast.toast("목표 연도를 선택하세요.", {
                    variant: "warning",
                    title: "입력 오류",
                });
                return false;
            }
            if (!this.editForm.targetEmissions) {
                this.$bvToast.toast("목표 배출량을 입력하세요.", { variant: "warning", title: "입력 오류" });
                return false;
            }
            if (!this.editForm.targetType) {
                this.$bvToast.toast("목표 유형을 선택하세요.", { variant: "warning", title: "입력 오류" });
                return false;
            }
            if (!this.editForm.complianceIdx) {
                this.$bvToast.toast("준수기준을 선택하세요.", { variant: "warning", title: "입력 오류" });
                return false;
            }
            return true;
        },
        async deleteGoal(goal) {
            const confirmed = await this.alertConfirmWarning(`"${goal.goalName}" 목표를 삭제하시겠습니까?`);

            if (!confirmed.value) return;

            try {
                const result = await targetService.deleteGoal(goal.targetIdx);
                this.$bvToast.toast(result?.message || "목표가 삭제되었습니다.", {
                    variant: "success",
                    title: "삭제 완료",
                });
                await this.loadGoals();
            } catch (error) {
                console.error("목표 삭제 실패:", error);
                this.$bvToast.toast(error.message || "목표 삭제에 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
            }
        },
        validateForm() {
            if (!this.form.goalName) {
                this.$bvToast.toast("목표 명칭을 입력하세요.", { variant: "warning", title: "입력 오류" });
                return false;
            }
            if (!this.form.baseYear) {
                this.$bvToast.toast("기준 연도를 선택하세요.", {
                    variant: "warning",
                    title: "입력 오류",
                });
                return false;
            }
            if (!this.form.baseEmissions) {
                this.$bvToast.toast("기준 배출량을 입력하세요.", { variant: "warning", title: "입력 오류" });
                return false;
            }
            if (!this.form.targetYear) {
                this.$bvToast.toast("목표 연도를 선택하세요.", {
                    variant: "warning",
                    title: "입력 오류",
                });
                return false;
            }
            if (!this.form.targetEmissions) {
                this.$bvToast.toast("목표 배출량을 입력하세요.", { variant: "warning", title: "입력 오류" });
                return false;
            }
            if (!this.form.targetType) {
                this.$bvToast.toast("목표 유형을 선택하세요.", { variant: "warning", title: "입력 오류" });
                return false;
            }
            if (!this.form.complianceIdx) {
                this.$bvToast.toast("준수기준을 선택하세요.", { variant: "warning", title: "입력 오류" });
                return false;
            }
            return true;
        },
        resetForm() {
            this.form = {
                goalName: "",
                orgIdx: null,
                baseYear: null,
                baseEmissions: null,
                targetYear: null,
                targetEmissions: null,
                targetType: "ABSOLUTE",
                complianceIdx: null,
                intensityDenom: null,
                goalStat: "ACTIVE",
                remarks: null,
            };
            this.editingGoalIdx = null;
        },
        getTargetTypeLabel(type) {
            return CarbonCodes.targetType.getLabel(type) || type;
        },
        getTargetTypeBadgeClass(type) {
            return type === "absolute" ? "badge badge-absolute" : "badge badge-intensity";
        },
        formatNumber(num) {
            if (!num && num !== 0) return "0.0";
            return Number(num).toLocaleString("ko-KR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        },
        getComplianceName(complianceIdx) {
            if (!complianceIdx) return null;
            const compliance = this.complianceList.find((c) => c.complianceIdx === complianceIdx);
            return compliance?.complianceName || null;
        },
        getOrgName(orgIdx) {
            if (orgIdx == null) return "-";
            const org = this.organizationList.find((o) => o.orgIdx === orgIdx);
            return org ? org.orgName || org.orgCode || `조직 #${orgIdx}` : `조직 #${orgIdx}`;
        },
        calculateGoalReductionRate(goal) {
            if (!goal.baseEmissions || !goal.targetEmissions) return "0.0";
            const rate = ((goal.baseEmissions - goal.targetEmissions) / goal.baseEmissions) * 100;
            return rate.toFixed(1);
        },
        getEmissionUnit() {
            if (this.form.targetType === "intensity") {
                return "원";
            }
            return TARGET_STORAGE_UNIT;
        },
        getEditFormEmissionUnit() {
            if (this.editForm.targetType === "intensity") {
                return "원";
            }
            return TARGET_STORAGE_UNIT;
        },
    },
};
</script>

<style scoped>
.goal-management {
    padding: 16px;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #f8f9fa;
    overflow: hidden;
}

.goal-btn-save {
    padding: 8px 15px;
    background: #52b788;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.goal-btn-save:hover:not(:disabled) {
    background: #40916c;
    transform: translateY(-1px);
}

.goal-btn-save:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
}

.goal-btn-save i.fa-plus {
    margin-right: 6px;
}

/* 입력 패널 영역 */
.goal-input-panels {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 12px;
    margin-bottom: 12px;
    flex-shrink: 0;
}

.input-panel {
    background: white;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    padding: 16px;
    display: flex;
    flex-direction: column;
}

.panel-title {
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
    margin: 0 0 12px 0;
    padding-left: 5px;
    border-left: 2px solid #52b788;
}

.panel-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 9px;
}

.panel-content.comparative-content {
    flex-direction: row;
    gap: 12px;
}

.goal-form-group {
    display: flex;
    flex-direction: column;
}

.goal-form-group label {
    font-size: 12px;
    font-weight: 600;
    color: #374151;
}

.form-control {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 12px;
    transition: all 0.2s;
    background: white;
}

.form-control:focus {
    outline: none;
    border-color: #52b788;
    box-shadow: 0 0 0 3px rgba(82, 183, 136, 0.1);
}

.input-with-unit {
    position: relative;
    display: flex;
    align-items: center;
}

.input-with-unit .form-control {
    padding-right: 45px;
}

.input-with-unit .unit-label {
    position: absolute;
    right: 12px;
    font-size: 12px;
    color: #6b7280;
    pointer-events: none;
    white-space: nowrap;
}

.remarks-textarea {
    resize: vertical;
    /* min-height: 80px; */
    font-family: inherit;
}

.data-subsection {
    flex: 1;
    padding: 12px;
    border-radius: 6px;
    background: #f8f9fa;
    border: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.data-subsection.highlight-subsection {
    background: #ecfdf5;
    border-color: #d1fae5;
}

.highlight-subsection .subsection-title {
    color: #52b788;
}

.subsection-title {
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
}

.remarks-section {
    display: flex;
    flex-direction: column;
    height: 100%;
}

/* DatePicker 스타일 */
:deep(.mx-datepicker) {
    width: 100%;
}

:deep(.mx-input-wrapper) {
    width: 100%;
}

:deep(.mx-input) {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 12px;
    transition: all 0.2s;
    background: white;
}

:deep(.mx-input:focus) {
    outline: none;
    border-color: #52b788;
    box-shadow: 0 0 0 3px rgba(82, 183, 136, 0.1);
}

:deep(.mx-datepicker-popup) {
    z-index: 1050;
}

/* 목표 목록 섹션 */
.goal-list-section {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    overflow: hidden;
    min-height: 0;
}

.loading-state,
.empty-state {
    padding: 20px;
    text-align: center;
    font-size: 12px;
    color: #9ca3af;
}

.spinner {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid #f3f4f6;
    border-top-color: #52b788;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-right: 6px;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.goal-table-container {
    flex: 1;
    overflow: auto;
    min-height: 0;
}

.goal-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
}

.goal-table thead {
    background: #f8f9fa;
    position: sticky;
    top: 0;
    z-index: 10;
}

.goal-table th {
    padding: 10px 12px;
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    border-bottom: 2px solid #e5e7eb;
    white-space: nowrap;
}

.goal-table td {
    padding: 10px 12px;
    border-bottom: 1px solid #f3f4f6;
    color: #374151;
}

.goal-table td.text-center {
    text-align: center;
}

.goal-table td.text-right {
    text-align: right;
}

.goal-table tbody tr:hover {
    background: #f9fafb;
}

.target-emission-cell {
    color: #52b788;
    font-weight: 600;
}

.reduction-rate-cell {
    color: #52b788;
    font-weight: 600;
}

.badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}

.badge-absolute {
    background: #d1fae5;
    color: #065f46;
}

.badge-intensity {
    background: #fef3c7;
    color: #92400e;
}

.action-buttons {
    display: flex;
    gap: 8px;
    justify-content: center;
}

.btn-action {
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: 0;
    font-size: 13px;
    cursor: pointer;
    transition: opacity 0.15s;
    background: transparent;
    color: #9ca3af;
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-action:hover {
    opacity: 0.7;
    color: #374151;
}

.btn-action.btn-edit:hover {
    color: #047857;
}

.btn-action.btn-delete:hover {
    color: #ef4444;
}

/* 모달 폼 스타일 */
.modal-form {
    padding: 8px 16px;
}

.modal-form .form-row {
    display: flex;
    gap: 12px;
}

.modal-form .form-group {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.modal-form .form-group.half {
    flex: 0 0 calc(50% - 6px);
}

.modal-form .form-group label {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 4px;
}

.modal-form .required {
    color: #ef4444;
}

.modal-form .form-control {
    padding: 6px 10px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 13px;
    transition: border-color 0.2s;
}

.modal-form .form-control:focus {
    outline: none;
    border-color: #52b788;
    box-shadow: 0 0 0 2px rgba(82, 183, 136, 0.1);
}

.modal-form .input-with-unit {
    position: relative;
    display: flex;
    align-items: center;
}

.modal-form .input-with-unit .form-control {
    padding-right: 60px;
}

.modal-form .input-with-unit .unit-label {
    position: absolute;
    right: 12px;
    font-size: 12px;
    color: #6b7280;
    pointer-events: none;
    white-space: nowrap;
}

.modal-form :deep(.mx-datepicker) {
    width: 100%;
}

.modal-form :deep(.mx-input-wrapper) {
    width: 100%;
}

.modal-form :deep(.mx-input) {
    width: 100%;
    padding: 6px 10px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 13px;
    transition: border-color 0.2s;
}

.modal-form :deep(.mx-input:focus) {
    outline: none;
    border-color: #52b788;
    box-shadow: 0 0 0 2px rgba(82, 183, 136, 0.1);
}

.modal-form .btn {
    padding: 8px 16px;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.modal-form .btn-secondary {
    background: #f3f4f6;
    color: #4b5563;
}

.modal-form .btn-secondary:hover {
    background: #e5e7eb;
}

.modal-form .btn-primary {
    background: #52b788;
    color: white;
}

.modal-form .btn-primary:hover:not(:disabled) {
    background: #40916c;
    transform: translateY(-1px);
}

.modal-form .btn-primary:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
}

/* b-modal 기본 버튼 스타일 커스터마이징 */
:deep(.modal-footer .btn-primary) {
    background: #52b788;
    border-color: #52b788;
    color: white;
}

:deep(.modal-footer .btn-primary:hover:not(:disabled)) {
    background: #40916c;
    border-color: #40916c;
    transform: translateY(-1px);
}

:deep(.modal-footer .btn-primary:disabled) {
    background: #9ca3af;
    border-color: #9ca3af;
    cursor: not-allowed;
    transform: none;
}
</style>
